import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DemoBar from '../../components/DemoBar';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (header: string) => {
    setCollapsedSections(prev => ({ ...prev, [header]: !prev[header] }));
  };

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname.startsWith(path);

  type MenuItem = { path: string; label: string; exact?: boolean };
  type MenuSection = { header: string; items: MenuItem[] };

  const menuSections: MenuSection[] = [
    {
      header: '',
      items: [
        { path: '/admin', label: 'Dashboard', exact: true },
      ],
    },
    {
      header: 'Core Content',
      items: [
        { path: '/admin/moves', label: 'Moves' },
        { path: '/admin/patterns', label: 'Patterns' },
        { path: '/admin/breakdowns', label: 'Breakdowns' },
        { path: '/admin/demonstrations', label: 'Demonstrations' },
      ],
    },
    {
      header: 'Reference Tables',
      items: [
        { path: '/admin/styles', label: 'Dance Styles' },
        { path: '/admin/skill-levels', label: 'Skill Levels' },
        { path: '/admin/move-types', label: 'Move Types' },
        { path: '/admin/dance-roles', label: 'Dance Roles' },
        { path: '/admin/breakdown-angles', label: 'Breakdown Angles' },
        { path: '/admin/breakdown-clip-types', label: 'Clip Types' },
      ],
    },
    {
      header: 'Users & Identity',
      items: [
        { path: '/admin/members', label: 'Members' },
        { path: '/admin/invites', label: 'Invites' },
        { path: '/admin/billing', label: 'Memberships & Billing' },
        { path: '/admin/instructors', label: 'Instructors' },
        { path: '/admin/dancers', label: 'Dancers' },
      ],
    },
  ];

  return (
    <>
    <DemoBar sticky />
    <div className="admin-layout">
      <button
        className="admin-sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle menu"
      >
        {sidebarOpen ? '✕' : '☰'}
      </button>
      {sidebarOpen && (
        <div className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}
      <div className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2>Admin Portal</h2>
          <Link to="/" className="back-to-site">← Back to Site</Link>
        </div>
        <nav className="admin-nav">
          {menuSections.map((section) => (
            <div key={section.header || 'top'} className="admin-nav-section">
              {section.header && (
                <button
                  className="admin-nav-section-header"
                  onClick={() => toggleSection(section.header)}
                >
                  <span>{section.header}</span>
                  <span className={`admin-nav-section-chevron ${collapsedSections[section.header] ? 'collapsed' : ''}`}>&#9662;</span>
                </button>
              )}
              {!collapsedSections[section.header] && (
                <ul>
                  {section.items.map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`admin-nav-link ${
                          item.exact
                            ? location.pathname === item.path
                              ? 'active'
                              : ''
                            : isActive(item.path)
                            ? 'active'
                            : ''
                        }`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </nav>
      </div>
      <div className="admin-main">
        <div className="admin-content">
          {children}
        </div>
      </div>
    </div>
    </>
  );
};

export default AdminLayout;